import axios from "axios";
import api from "../../utils/axios";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

const publicGameApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  timeout: 15000,
});

export async function accessGame(payload) {
  const { data } = await publicGameApi.post("/v1/games/access", payload);
  return data;
}

function buildGameAccessConfig(gameAccessToken) {
  const token =
    typeof gameAccessToken === "string" ? gameAccessToken.trim() : "";

  return {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-Game-Access-Token": token,
    },
  };
}

function buildGameGuestConfig(gameToken) {
  const token = typeof gameToken === "string" ? gameToken.trim() : "";

  return {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-Game-Token": token,
    },
  };
}

export function isGameAccessUnauthorized(error) {
  return error?.response?.status === 401;
}

export function isGameGuestUnauthorized(error) {
  return error?.response?.status === 401;
}

function cleanQueryParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params || {}).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
}

export async function registerGamePlayer(gameId, payload) {
  const { gameAccessToken, ...body } = payload || {};
  const token =
    typeof gameAccessToken === "string" ? gameAccessToken.trim() : "";
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/players/register`,
    {
      ...body,
      gameAccessToken: token,
    },
    buildGameAccessConfig(token),
  );
  return data;
}

export async function loginExistingGamePlayer(
  gameId,
  playerId,
  gameAccessToken,
) {
  const token =
    typeof gameAccessToken === "string" ? gameAccessToken.trim() : "";
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/players/${playerId}/login`,
    { gameAccessToken: token },
    buildGameAccessConfig(token),
  );
  return data;
}

export async function presignGameAvatarUpload(
  gameId,
  gameAccessToken,
  payload,
) {
  const token =
    typeof gameAccessToken === "string" ? gameAccessToken.trim() : "";
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/player-avatar/presign`,
    {
      ...(payload || {}),
      gameAccessToken: token,
    },
    buildGameAccessConfig(token),
  );
  return data;
}

export async function finalizeGameAvatarUpload(
  gameId,
  gameAccessToken,
  payload,
) {
  const token =
    typeof gameAccessToken === "string" ? gameAccessToken.trim() : "";
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/player-avatar/finalize`,
    {
      ...(payload || {}),
      gameAccessToken: token,
    },
    buildGameAccessConfig(token),
  );
  return data;
}

export async function getGameLobby(gameId, gameToken) {
  const { data } = await publicGameApi.get(
    `/v1/games/${gameId}/lobby`,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function getCurrentTurn(gameId, gameToken) {
  const { data } = await publicGameApi.get(
    `/v1/games/${gameId}/current-turn`,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function submitTurnAnswer(gameId, turnId, selection, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/turns/${turnId}/answer`,
    { selection },
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function submitTurnGuess(gameId, turnId, selection, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/turns/${turnId}/guess`,
    { selection },
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function getCurrentPenalty(gameId, gameToken) {
  const { data } = await publicGameApi.get(
    `/v1/games/${gameId}/current-penalty`,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function chooseTurnPenaltyMode(gameId, turnId, mode, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/turns/${turnId}/penalty-mode`,
    { mode },
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function confirmTurnCoinPaid(gameId, turnId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/turns/${turnId}/coin-paid`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function confirmTurnDareDone(gameId, turnId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/turns/${turnId}/dare-done`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function logoutGamePlayer(gameId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/logout`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function getMyPartnerInbox(gameId, gameToken) {
  const { data } = await publicGameApi.get(
    `/v1/games/${gameId}/partner-requests/me`,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function sendPartnerRequest(gameId, payload, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/partner-requests`,
    payload,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function acceptPartnerRequest(gameId, requestId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/partner-requests/${requestId}/accept`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function rejectPartnerRequest(gameId, requestId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/partner-requests/${requestId}/reject`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function cancelPartnerRequest(gameId, requestId, gameToken) {
  const { data } = await publicGameApi.post(
    `/v1/games/${gameId}/partner-requests/${requestId}/cancel`,
    {},
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function listAdminGames() {
  const { data } = await api.get("/v1/admin/games");
  return data;
}

export async function createAdminGame(payload) {
  const { data } = await api.post("/v1/admin/games", payload);
  return data;
}

export async function getAdminGameById(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}`);
  return data;
}

export async function updateAdminGame(gameId, payload) {
  const { data } = await api.patch(`/v1/admin/games/${gameId}`, payload);
  return data;
}

export async function getAdminGameState(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/state`);
  return data;
}

export async function openRegistration(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/open-registration`,
    payload,
  );
  return data;
}

export async function closeRegistration(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/close-registration`,
    payload,
  );
  return data;
}

export async function startRoundOne(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/start-round-one`,
    payload,
  );
  return data;
}

export async function pauseGame(gameId, payload = {}) {
  const { data } = await api.post(`/v1/admin/games/${gameId}/pause`, payload);
  return data;
}

export async function getAdminCurrentTurn(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/current-turn`);
  return data;
}

export async function createAdminNextTurn(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/next-turn`,
    payload,
  );
  return data;
}

export async function completeAdminTurnResult(gameId, turnId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/turns/${turnId}/complete-result`,
    payload,
  );
  return data;
}

export async function openAdminPenalty(gameId, turnId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/turns/${turnId}/open-penalty`,
    payload,
  );
  return data;
}

export async function changeAdminRound(gameId, roundNumber, payload = {}) {
  const { data } = await api.post(`/v1/admin/games/${gameId}/change-round`, {
    ...payload,
    roundNumber,
  });
  return data;
}

export async function resolveAdminDare(gameId, turnId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/turns/${turnId}/admin-resolve-dare`,
    payload,
  );
  return data;
}

export async function getAdminRoundState(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/round-state`);
  return data;
}

export async function listAdminGameQuestions(params = {}) {
  const { data } = await api.get("/v1/admin/game-questions", { params });
  return data;
}

export async function createAdminGameQuestion(payload) {
  const { data } = await api.post("/v1/admin/game-questions", payload);
  return data;
}

export async function getAdminGameQuestionById(questionId) {
  const { data } = await api.get(`/v1/admin/game-questions/${questionId}`);
  return data;
}

export async function updateAdminGameQuestion(questionId, payload) {
  const { data } = await api.patch(
    `/v1/admin/game-questions/${questionId}`,
    payload,
  );
  return data;
}

export async function presignAdminGameQuestionImage(
  questionId,
  imageType,
  payload,
) {
  const { data } = await api.post(
    `/v1/admin/game-questions/${questionId}/presign/${imageType}`,
    payload,
  );
  return data;
}

export async function updateAdminGameQuestionStatus(questionId, isActive) {
  const { data } = await api.patch(
    `/v1/admin/game-questions/${questionId}/status`,
    {
      isActive,
    },
  );
  return data;
}

export async function getMyGameStats(gameId, gameToken) {
  const { data } = await publicGameApi.get(
    `/v1/games/${gameId}/my-stats`,
    buildGameGuestConfig(gameToken),
  );
  return data;
}

export async function getGameLeaderboard(gameId, gameToken, params = {}) {
  const { data } = await publicGameApi.get(`/v1/games/${gameId}/leaderboard`, {
    ...buildGameGuestConfig(gameToken),
    params: cleanQueryParams(params),
  });
  return data;
}

export async function getMyGameHistory(gameId, gameToken, params = {}) {
  const { data } = await publicGameApi.get(`/v1/games/${gameId}/my-history`, {
    ...buildGameGuestConfig(gameToken),
    params: cleanQueryParams(params),
  });
  return data;
}

export async function getAdminGameHealth(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/health`);
  return data;
}

export async function getAdminGameReadiness(gameId) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/readiness`);
  return data;
}

export async function getAdminGameHistory(gameId, params = {}) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/history`, {
    params: cleanQueryParams(params),
  });
  return data;
}

export async function getAdminGameReport(gameId, params = {}) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/report`, {
    params: cleanQueryParams(params),
  });
  return data;
}

export async function getAdminGameAudit(gameId, params = {}) {
  const { data } = await api.get(`/v1/admin/games/${gameId}/audit`, {
    params: cleanQueryParams(params),
  });
  return data;
}

export async function getAdminOperationalWarnings(gameId) {
  const { data } = await api.get(
    `/v1/admin/games/${gameId}/operational-warnings`,
  );
  return data;
}

export async function skipAdminActiveTurn(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/recovery/skip-active-turn`,
    payload,
  );
  return data;
}

export async function forceAdminCompleteResult(gameId, turnId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/turns/${turnId}/recovery/force-complete-result`,
    payload,
  );
  return data;
}

export async function forceAdminResolvePenalty(gameId, turnId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/turns/${turnId}/recovery/force-resolve-penalty`,
    payload,
  );
  return data;
}

export async function resetAdminLiveState(gameId, payload = {}) {
  const { data } = await api.post(
    `/v1/admin/games/${gameId}/recovery/reset-live-state`,
    payload,
  );
  return data;
}
